import React, { useState, useEffect } from "react";
import { BrowserRouter as Router } from "react-router-dom";

import { ErrorPage } from "./containers/ErrorPage";
import { LMSCheckoutPage } from "./containers/LMSCheckoutPage";
import {
  LinkProvider,
  LMSTranslationsProvider,
  UserProvider,
} from "./context";
import { UtilService } from "./services";
import AppRouter from "./AppRouter";
import {
  msalInstance,
  LoadingIcon,
  CommonTranslationsProvider,
  ErrorProvider,
  TranslationsProvider,
  RegionProvider,
} from "tds_shared_ui";
import "./App.css";

const App = () => {
  const [loadingRegion, setLoadingRegion] = useState(true);
  const [regionData, setRegionData] = useState(null);
  const [error, setError] = useState(null);
  const [locale, setLocale] = useState(null);
  const [showLmsCheckout, setShowLmsCheckout] = useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        await msalInstance.initialize();
        const tdslang = sessionStorage.getItem("tdslang");

        if (window.location.pathname === "/lms") {
          setShowLmsCheckout(true);
          setLocale("en");
          try {
            const data = await UtilService.getRegion();
            setRegionData(data);
          } catch (e) {
            console.error("Failed to fetch region data:", e);
            setError("Unable to load region data. Please try again later.");
          }
        } else {
          setLoadingRegion(true);
          try {
            const data = await UtilService.getRegion();
            const lang = data.locale.split(/[-_]+/)[0];
            setLoadingRegion(false);
            setRegionData(data);
            setLocale(tdslang || lang);
          } catch (err) {
            let errorMsg = "Unknown error occurred. Please refresh page to try again.";
            if (err?.category === "REGION") {
              errorMsg = "ACDelco Technical Delivery System is not supported in your region";
            }
            setLoadingRegion(false);
            setError(errorMsg);
          }
        }
      } catch (e) {
        console.error("Authentication initialization failed:", e);
        setError("Authentication initialization failed.");
      }
    };
    initializeApp();
  }, []);

  const handleLanguageChange = (langCode) => {
    sessionStorage.setItem("tdslang", langCode);
    UtilService.updateRegion()
      .then((data) => {
        setRegionData(data);
        setLocale(langCode);
      })
      .catch((e) => {
        console.error("Failed to change language:", e);
        setError("Failed to change language. Please try again.");
      });
  };

  const handleRegionRefresh = () => {
    return UtilService.updateRegion()
      .then((data) => {
        setRegionData(data);
        const tdslang = sessionStorage.getItem("tdslang");
        const lang = data.locale.split(/[-_]+/)[0];
        setLocale(tdslang || lang);
      })
      .catch((e) => {
        console.error("Failed to refresh region:", e);
        setError("Failed to refresh region. Please try again.");
        throw e;
      });
  };

  const renderNotSupported = () => (
    <CommonTranslationsProvider locale={locale}>
      <ErrorPage error={error} />
    </CommonTranslationsProvider>
  );

  const renderLoading = () => (
    <div className="container">
      <LoadingIcon display={true} size="3x" />
    </div>
  );

  const renderLmsCheckout = () => (
    <RegionProvider regionData={regionData}>
      <LMSTranslationsProvider locale={locale}>
        <CommonTranslationsProvider locale={locale}>
          <ErrorProvider>
            <LMSCheckoutPage />
          </ErrorProvider>
        </CommonTranslationsProvider>
      </LMSTranslationsProvider>
    </RegionProvider>
  );

  const renderApp = () => (
    <Router>
      <TranslationsProvider locale={locale}>
        <CommonTranslationsProvider locale={locale}>
          <ErrorProvider>
            <UserProvider
              onLocaleChange={handleLanguageChange}
              locale={locale}
              regionData={regionData}
            >
              <RegionProvider regionData={regionData} locale={locale}>
                <LinkProvider locale={locale}>
                  <AppRouter
                    onLanguageChange={handleLanguageChange}
                    onRegionRefresh={handleRegionRefresh}
                  />
                </LinkProvider>
              </RegionProvider>
            </UserProvider>
          </ErrorProvider>
        </CommonTranslationsProvider>
      </TranslationsProvider>
    </Router>
  );

  if (showLmsCheckout) {
    return renderLmsCheckout();
  } else if (loadingRegion) {
    return renderLoading();
  } else if (regionData === null) {
    return renderNotSupported();
  } else {
    return renderApp();
  }
};

export default App;